import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSubscriptionByEmail, type SubscriptionStatus } from "@/lib/subscription";

/**
 * Tracks the signed-in user's email + subscription status.
 *
 * Returned fields:
 * - `email`            — signed-in user email, or null
 * - `subscription`     — full subscription row, or null
 * - `isActive`         — paid/trial access right now
 * - `inTrial`          — currently inside the 14-day free trial
 * - `daysLeftInTrial`  — whole days remaining in the trial
 * - `expiredTrial`     — trial ended without a payment method (soft-lock state)
 * - `loading`          — true until BOTH auth + subscription have resolved
 */
export const useSubscription = () => {
  const [email, setEmail] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [subReady, setSubReady] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setEmail(session?.user?.email ?? null);
      setAuthReady(true);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setEmail(session?.user?.email ?? null);
      setAuthReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!authReady) return;
    let cancelled = false;
    if (!email) {
      setSubscription(null);
      setSubReady(true);
      return;
    }
    setSubReady(false);
    getSubscriptionByEmail(email)
      .then((s) => {
        if (!cancelled) setSubscription(s);
      })
      .catch((error) => {
        console.error("[useSubscription] lookup failed", error);
        if (!cancelled) setSubscription(null);
      })
      .finally(() => {
        if (!cancelled) setSubReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [email, authReady]);

  const refresh = async () => {
    if (!email) return;
    try {
      const s = await getSubscriptionByEmail(email);
      setSubscription(s);
    } catch (error) {
      console.error("[useSubscription] refresh failed", error);
      setSubscription(null);
    }
  };

  return {
    email,
    subscription,
    isActive: !!subscription?.active,
    inTrial: !!subscription?.inTrial,
    daysLeftInTrial: subscription?.daysLeftInTrial ?? 0,
    expiredTrial: !!subscription?.expiredTrial,
    plan: subscription?.plan ?? null,
    loading: !authReady || !subReady,
    refresh,
  };
};
